import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  SelectChangeEvent,
} from '@mui/material';
import { getClients, updateUserIndex } from '../../api/api';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/appStore';
import { login } from '../../store/authSlice';
import { showSnackbar } from '../../store/snackBarSlice';

interface SettingsModalProps {
  open: boolean;
  onClose: () => void;
}

interface Client {
  client_id: number;
  client_name: string;
  storage_class: string;
  email_domain: string;
  is_current?: boolean;
  archetype: string | null;
}

interface User {
  user_id: number;
  client_id?: number;
  client_name?: string;
  [key: string]: any; // For other properties that might exist on the user object
}

const SettingsModal: React.FC<SettingsModalProps> = ({ open, onClose }) => {
  const [language, setLanguage] = useState<string>('en');
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [selectedClientName, setSelectedClientName] = useState<string>('');
  const [clients, setClients] = useState<Client[]>([]);
  const [archetypes, setArchetypes] = useState<(string | null)[]>([]);
  const [selectedArchetype, setSelectedArchetype] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [updating, setUpdating] = useState<boolean>(false);

  const user = useSelector((state: RootState) => state.auth.user as User | null);
  const roles = Array.isArray((user as any)?.roles) ? (user as any).roles : [];
  const isAdmin =
    roles.some((role: string) => String(role).toLowerCase() === 'admin') ||
    String((user as any)?.role || '').toLowerCase() === 'admin';
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchClients = async () => {
      try {
        setLoading(true);
        // Use the existing getClients function with empty search, page 1, and a large page size
        const response = await getClients('', 1, 100);
        if (response && response.success && response.data) {
          setClients(response.data);

          // Extract unique archetypes from clients
          const uniqueArchetypes = Array.from(
            new Set(response.data.map((client: Client) => client.archetype))
          );
          setArchetypes(uniqueArchetypes as (string | null)[]);

          // First, try to find the client with is_current=true
          const currentClient = response.data.find((client: Client) =>
            client.is_current === true
          );

          if (currentClient) {
            // If found, select the client with is_current=true
            setSelectedClientId(currentClient.client_id.toString());
            setSelectedClientName(currentClient.client_name);
            setSelectedArchetype(currentClient.archetype);
          } else if (user && user.client_id) {
            // Fallback to the user's client_id if is_current is not available
            const userClient = response.data.find((client: Client) =>
              client.client_id.toString() === user?.client_id?.toString()
            );

            if (userClient) {
              setSelectedClientId(userClient.client_id.toString());
              setSelectedClientName(userClient.client_name);
              setSelectedArchetype(userClient.archetype);
            } else if (response.data.length > 0) {
              // Otherwise set the first available client
              setSelectedClientId(response.data[0].client_id.toString());
              setSelectedClientName(response.data[0].client_name);
              setSelectedArchetype(response.data[0].archetype);
            }
          } else if (response.data.length > 0) {
            // If no client_id in user, set the first available client
            setSelectedClientId(response.data[0].client_id.toString());
            setSelectedClientName(response.data[0].client_name);
            setSelectedArchetype(response.data[0].archetype);
          }
        }
      } catch (error) {
        console.error('Error fetching clients:', error);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchClients();
    }
  }, [open, user]);

  const handleLanguageChange = (event: SelectChangeEvent) => {
    setLanguage(event.target.value as string);
  };

  const handleArchetypeChange = (event: SelectChangeEvent) => {
    const archetype = event.target.value === "null" ? null : event.target.value;
    setSelectedArchetype(archetype);

    if (!isAdmin) {
      return;
    }

    // When archetype changes, we need to reset the selected client
    // Find the first client with the selected archetype
    const filteredClients = clients.filter(
      (client: Client) => client.archetype === archetype || (archetype === null && client.archetype === null)
    );

    if (filteredClients.length > 0) {
      setSelectedClientId(filteredClients[0].client_id.toString());
      setSelectedClientName(filteredClients[0].client_name);
    } else {
      // If no clients with this archetype, reset selection
      setSelectedClientId('');
      setSelectedClientName('');
    }
  };

  const handleIndexChange = (event: SelectChangeEvent) => {
    const clientId = event.target.value as string;
    setSelectedClientId(clientId);

    // Find the client name that corresponds to the selected ID
    const selectedClient = clients.find((client: Client) => client.client_id.toString() === clientId);
    if (selectedClient) {
      setSelectedClientName(selectedClient.client_name);
    }
  };

  const handleSave = async () => {
    if (!user || !user.user_id) {
      dispatch(showSnackbar({ message: 'User information not available', severity: 'error' }));
      return;
    }

    try {
      setUpdating(true);

      // Check if the index has changed
      const isIndexChanged = user.client_id?.toString() !== selectedClientId;

      // Use the new updateUserIndex endpoint
      const response = await updateUserIndex(user.user_id, selectedClientId);

      if (response && response.success) {
        if (isIndexChanged && response.payload?.access_token) {
          // If index changed, update the token by page refresh
          const newToken = response.payload.access_token;
          const currentUserData = JSON.parse(localStorage.getItem('user') || '{}');

          const updatedUserData = {
            ...currentUserData,
            access_token: newToken,
            client_id: response.payload.user.client_id,
            client_name: response.payload.user.client_name,
          };

          // Update localStorage with user data including new token
          localStorage.setItem('user', JSON.stringify(updatedUserData));

          // Update Redux store with new user data
          dispatch(login(updatedUserData));

          // Show success message
          dispatch(showSnackbar({
            message: 'Index updated successfully. Page will refresh to apply changes.',
            severity: 'success'
          }));

          // Close the modal
          onClose();

          // Refresh the page to ensure all components use the new index
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        } else {
          // If index didn't change, just update the user info as before
          const currentUserData = JSON.parse(localStorage.getItem('user') || '{}');
          const updatedUser = {
            ...currentUserData,
            client_id: parseInt(selectedClientId),
            client_name: selectedClientName
          };

          // Update localStorage
          localStorage.setItem('user', JSON.stringify(updatedUser));

          // Update Redux store
          dispatch(login(updatedUser));

          // Show success message using the application's snackbar system
          dispatch(showSnackbar({ message: 'Settings updated successfully', severity: 'success' }));

          // Close the modal
          onClose();
        }
      } else {
        const errorMessage = response?.payload || 'Failed to update settings';
        dispatch(showSnackbar({ message: errorMessage, severity: 'error' }));
      }
    } catch (error) {
      console.error('Error updating user settings:', error);
      dispatch(showSnackbar({ message: 'An error occurred while updating settings', severity: 'error' }));
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Settings</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="language-select-label">Language</InputLabel>
            <Select
              labelId="language-select-label"
              id="language-select"
              value={language}
              label="Language"
              onChange={handleLanguageChange}
            >
              <MenuItem value="en">English</MenuItem>
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              Currently only English is supported
            </Typography>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="archetype-select-label">Archetype</InputLabel>
            <Select
              labelId="archetype-select-label"
              id="archetype-select"
              value={selectedArchetype === null ? "null" : selectedArchetype}
              label="Archetype"
              onChange={handleArchetypeChange}
              disabled={loading}
            >
              {loading ? (
                <MenuItem value="null">Loading...</MenuItem>
              ) : (
                archetypes.map((archetype, index) => (
                  <MenuItem key={index} value={archetype === null ? "null" : archetype}>
                    {archetype === null ? "None" : archetype}
                  </MenuItem>
                ))
              )}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              Group indexes by logical archetype
            </Typography>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="index-select-label">Index</InputLabel>
            <Select
              labelId="index-select-label"
              id="index-select"
              value={selectedClientId}
              label="Index"
              onChange={handleIndexChange}
              disabled={loading || !isAdmin}
            >
              {loading ? (
                <MenuItem value="">Loading...</MenuItem>
              ) : (
                clients
                  .filter(client =>
                    selectedArchetype === null
                      ? client.archetype === null
                      : client.archetype === selectedArchetype
                  )
                  .map((client) => (
                    <MenuItem key={client.client_id} value={client.client_id.toString()}>
                      {client.client_name}
                    </MenuItem>
                  ))
              )}
            </Select>
            {!isAdmin && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Only Admin users can change index
              </Typography>
            )}
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={updating}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={updating || loading}
        >
          {updating ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SettingsModal;
